/**
 * Automatic Workflow Service
 * Handles automatic state transitions for supplier and customer workflows
 * based on business events and triggers
 */

export interface AutoTransitionRule {
  fromState: string;
  toState: string;
  trigger: string;
  condition?: () => boolean;
  action?: () => Promise<void>;
  description: string;
}

export class AutomaticWorkflowService {
  
  /**
   * Supplier workflow automatic transition rules
   */
  static SUPPLIER_AUTO_TRANSITIONS: AutoTransitionRule[] = [
    {
      fromState: 'supplier_quote_generated',
      toState: 'supplier_contacted',
      trigger: 'EMAIL_PREVIEW_GENERATED',
      description: 'Automatically transition when email preview is generated (quote ready to send)'
    },
    {
      fromState: 'supplier_contacted',
      toState: 'supplier_response_received',
      trigger: 'EMAIL_RESPONSE_DETECTED',
      description: 'Automatically transition when supplier email response is detected'
    },
    {
      fromState: 'supplier_response_received', 
      toState: 'quote_selected',
      trigger: 'QUOTE_PARSED_AND_SELECTED',
      description: 'Automatically transition when quote is parsed and selected via EmailQuoteSelectionDialog'
    },
    {
      fromState: 'quote_selected',
      toState: 'converted_to_order',
      trigger: 'ORDER_CREATED',
      description: 'Automatically transition when quote is converted to order'
    }
  ];

  /**
   * Customer workflow automatic transition rules
   */
  static CUSTOMER_AUTO_TRANSITIONS: AutoTransitionRule[] = [
    {
      fromState: 'case_created',
      toState: 'preprocessing_decision',
      trigger: 'CASE_INITIALIZED',
      description: 'Automatically transition to preprocessing decision after case creation'
    },
    {
      fromState: 'preprocessing_needed',
      toState: 'waiting_for_customer_decision',
      trigger: 'SUPPLIER_QUOTES_RECEIVED',
      description: 'Automatically transition when all supplier quotes are received'
    }
  ];

  /**
   * Check if a workflow state should automatically transition
   */
  static shouldAutoTransition(caseType: 'customer_case' | 'supplier_case', currentState: string, trigger: string): AutoTransitionRule | null {
    const rules = caseType === 'supplier_case' 
      ? this.SUPPLIER_AUTO_TRANSITIONS 
      : this.CUSTOMER_AUTO_TRANSITIONS;
    
    return rules.find(rule => 
      rule.fromState === currentState && 
      rule.trigger === trigger
    ) || null;
  }

  /**
   * Execute automatic workflow transition
   */
  static async executeAutoTransition(
    caseId: string, 
    caseType: 'customer_case' | 'supplier_case',
    currentState: string, 
    trigger: string,
    metadata?: any
  ): Promise<boolean> {
    try {
      const rule = this.shouldAutoTransition(caseType, currentState, trigger);
      
      if (!rule) {
        console.log(`🔄 No auto-transition rule for ${caseType} ${currentState} with trigger ${trigger}`);
        return false;
      }

      console.log(`🚀 EXECUTING AUTO-TRANSITION: ${rule.fromState} → ${rule.toState} (${trigger})`);

      // Check condition if defined
      if (rule.condition && !rule.condition()) {
        console.log(`❌ Auto-transition condition failed for ${rule.fromState} → ${rule.toState}`);
        return false;
      }

      // Execute the transition
      const response = await fetch(`/api/v3/workflow/cases/${caseId}/transition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to_state: rule.toState,
          metadata: {
            ...metadata,
            auto_transition: true,
            trigger: trigger,
            rule_description: rule.description,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        console.log(`✅ AUTO-TRANSITION SUCCESS: ${rule.fromState} → ${rule.toState} for case ${caseId}`);
        
        // Execute post-transition action if defined
        if (rule.action) {
          await rule.action();
        }
        
        return true;
      } else {
        throw new Error(data.message || 'Unknown error');
      }

    } catch (error: any) {
      console.error(`❌ Auto-transition failed for case ${caseId}:`, error);
      return false;
    }
  }

  /**
   * Trigger automatic transitions for supplier workflow events
   */
  static async triggerSupplierEvent(
    caseId: string, 
    currentState: string, 
    event: 'EMAIL_PREVIEW_GENERATED' | 'EMAIL_RESPONSE_DETECTED' | 'QUOTE_PARSED_AND_SELECTED' | 'ORDER_CREATED',
    metadata?: any
  ): Promise<boolean> {
    return this.executeAutoTransition(caseId, 'supplier_case', currentState, event, metadata);
  }

  /**
   * Trigger automatic transitions for customer workflow events  
   */
  static async triggerCustomerEvent(
    caseId: string, 
    currentState: string, 
    event: 'CASE_INITIALIZED' | 'SUPPLIER_QUOTES_RECEIVED',
    metadata?: any
  ): Promise<boolean> {
    return this.executeAutoTransition(caseId, 'customer_case', currentState, event, metadata);
  }

  /**
   * Get all available auto-transition triggers for a workflow state
   */
  static getAvailableTriggers(caseType: 'customer_case' | 'supplier_case', currentState: string): AutoTransitionRule[] {
    const rules = caseType === 'supplier_case' 
      ? this.SUPPLIER_AUTO_TRANSITIONS 
      : this.CUSTOMER_AUTO_TRANSITIONS;
    
    return rules.filter(rule => rule.fromState === currentState);
  }

  /**
   * Check if state has automatic transitions available
   */
  static hasAutoTransitions(caseType: 'customer_case' | 'supplier_case', currentState: string): boolean {
    return this.getAvailableTriggers(caseType, currentState).length > 0;
  }
}
